(() => {
  const normalize = (value = '') =>
    String(value)
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');

  const aliases = new Map([
    ['azure', 'azure'], ['azuree', 'azure'], ['azure bay', 'azure'], ['bleu azure', 'azure'], ['azur', 'azure'],
    ['rose pale', 'rose pale'], ['blush pink', 'rose pale'], ['blush', 'rose pale'],
    ['pistache', 'pistache'], ['pistachio', 'pistache'],
    ['tiffany', 'tiffany'], ['tiffany blue', 'tiffany'],
    ['menthe', 'menthe'], ['mint', 'menthe'],
    ['lilas', 'lilas'], ['lilac', 'lilas'],
    ['tangerine', 'tangerine'], ['orange', 'tangerine']
  ]);

  const canonical = (value = '') => aliases.get(normalize(value)) || normalize(value);
  const isCommon = (value = '') => ['commun', 'commune', 'common', 'all', 'tous', 'toutes'].includes(canonical(value));

  // V520: only Deep Tones and Firkin Leather use this additional media attribution.
  // A color's featured image is authoritative. Other images must explicitly name a color
  // in their alt text/filename; otherwise we hide them rather than mix colorways.
  const groupedColor = (value, map) => {
    const cleaned = ` ${normalize(value).replace(/[^a-z0-9]+/g, ' ').trim()} `;
    if (!cleaned.trim()) return '';
    const names = [...new Set(map.map((item) => canonical(item.color || '')).filter(Boolean))]
      .sort((a, b) => b.length - a.length);
    const matches = names.filter((name) => {
      const needle = ` ${normalize(name).replace(/[^a-z0-9]+/g, ' ').trim()} `;
      return needle.trim() && cleaned.includes(needle);
    });
    if (!matches.length) return '';
    // Prefer "old black" over an ambiguous "black" substring. Do not guess
    // when the caption describes two unrelated colors.
    const preferred = matches[0];
    if (matches.some((name) => name !== preferred && !preferred.includes(name))) return '';
    return preferred;
  };

  const classifyMedia = (slide, map) => {
    const mediaId = String(slide.dataset.mediaId || '').split('-').pop();
    const owners = [...new Set(map.filter((item) => item.mediaId != null && String(item.mediaId) === mediaId)
      .map((item) => canonical(item.color || '')).filter(Boolean))];
    if (owners.length) return owners.join('|');

    const caption = slide.dataset.aoraMediaAlt || '';
    if (isCommon(caption)) return 'common';
    const fromCaption = groupedColor(caption, map);
    if (fromCaption) return fromCaption;

    // Shopify CDN filenames are useful when the uploaded image has no ALT text.
    const img = slide.querySelector('img');
    const filename = (img?.getAttribute('src') || '').split('?')[0].split('/').pop() || '';
    const fromFilename = groupedColor(filename, map);
    return fromFilename || '__unassigned';
  };

  const parseMap = (gallery) => {
    try { return JSON.parse(gallery?.querySelector('[data-aora-variant-map]')?.textContent || '[]'); }
    catch (_) { return []; }
  };

  const currentVariantId = (productInfo, gallery) => {
    const formValue = productInfo.querySelector('form [name="id"], [name="id"]')?.value;
    if (formValue) return String(formValue);
    const datasetValue = gallery?.dataset.aoraSelectedVariant;
    if (datasetValue) return String(datasetValue);
    return String(new URL(window.location.href).searchParams.get('variant') || '');
  };

  const selectedColor = (productInfo, gallery) => {
    // The checked colour control is the source of truth immediately after a tap.
    // Shopify's hidden variant id can lag behind briefly during section updates.
    const checked = [...productInfo.querySelectorAll('variant-selects input[type="radio"]:checked')]
      .find((input) => /couleur|color/i.test(input.dataset.optionName || input.name || input.closest('.product-form__input')?.textContent || ''))
      || productInfo.querySelector('variant-selects input[type="radio"]:checked');
    if (checked?.value) return canonical(checked.value);

    const select = [...productInfo.querySelectorAll('variant-selects select')]
      .find((node) => /couleur|color/i.test(node.name || node.closest('.product-form__input')?.textContent || ''))
      || productInfo.querySelector('variant-selects select');
    if (select?.value) return canonical(select.value);

    const map = parseMap(gallery);
    const id = currentVariantId(productInfo, gallery);
    const mapped = map.find((item) => String(item.id) === id);
    return mapped?.color ? canonical(mapped.color) : '';
  };

  class AoraVariantGallery {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.index = 0;
      this.visibleSlides = [];
      this.programmaticScroll = false;
      this.scrollTimer = null;
      this.mutationTimer = null;
      this.boundGallery = null;
      this.abort = new AbortController();
      this.bindProductEvents();
      this.attachGallery();
      this.filter(false, true);
    }

    attachGallery() {
      const nextGallery = this.productInfo.querySelector('media-gallery');
      if (!nextGallery) return false;
      if (this.boundGallery === nextGallery) {
        this.refreshDOM();
        return true;
      }

      this.galleryAbort?.abort();
      this.galleryAbort = new AbortController();
      this.gallery = nextGallery;
      this.boundGallery = nextGallery;
      this.strict = nextGallery.dataset.aoraStrict === 'true';
      this.colorLocked = nextGallery.dataset.aoraColorLocked === 'true';
      this.refreshDOM();
      if (this.strict) {
        this.bindGalleryEvents();
        // Media replacement is observed by bindProductEvents; no second observer is needed.
      }
      return true;
    }

    refreshDOM() {
      if (!this.gallery) return;
      this.mainList = this.gallery.querySelector('.product__media-list');
      this.slides = [...this.gallery.querySelectorAll('.product__media-item[data-aora-gallery-group]')];
      this.thumbs = [...this.gallery.querySelectorAll('.thumbnail-list__item[data-aora-gallery-group]')];
      if (this.colorLocked) {
        const map = parseMap(this.gallery);
        this.slides.forEach((slide) => {
          slide.dataset.aoraGalleryGroup = classifyMedia(slide, map);
        });
        // Use photo IDs for the dots as well, not independently guessed captions.
        const groups = new Map(this.slides.map((slide) => [slide.dataset.mediaId, slide.dataset.aoraGalleryGroup]));
        this.thumbs.forEach((thumb) => {
          thumb.dataset.aoraGalleryGroup = groups.get(thumb.dataset.target) || '__unassigned';
        });
      }
    }

    matches(element, color) {
      const group = canonical(element.dataset.aoraGalleryGroup || '');
      if (this.colorLocked && group === '__unassigned') return false;
      return isCommon(group) || (!!group && group.split('|').includes(color));
    }

    mappedVariant() {
      const map = parseMap(this.gallery);
      const color = selectedColor(this.productInfo, this.gallery);
      const byColor = map.find((item) => canonical(item.color || '') === color);
      if (byColor) return byColor;
      const id = currentVariantId(this.productInfo, this.gallery);
      return map.find((item) => String(item.id) === String(id));
    }

    filter(smooth = false, forceStart = false) {
      if (!this.attachGallery()) return;
      if (!this.strict) return;
      const color = selectedColor(this.productInfo, this.gallery);
      if (!color || !this.slides.length) return;

      this.gallery.dataset.aoraSelectedVariant = currentVariantId(this.productInfo, this.gallery);

      this.slides.forEach((slide) => {
        const visible = this.matches(slide, color);
        slide.hidden = !visible;
        slide.classList.toggle('aora-media-hidden', !visible);
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
      this.thumbs.forEach((thumb) => {
        const visible = this.matches(thumb, color);
        thumb.hidden = !visible;
        thumb.classList.toggle('aora-media-hidden', !visible);
        thumb.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });

      this.visibleSlides = this.slides.filter((slide) => !slide.hidden);
      const mapped = this.mappedVariant();

      if (!this.visibleSlides.length) {
        const fallback = mapped?.mediaId
          ? this.slides.find((slide) => String(slide.dataset.mediaId || '').endsWith('-' + mapped.mediaId))
          : null;
        this.slides.forEach((slide) => {
          const visible = slide === fallback;
          slide.hidden = !visible;
          slide.classList.toggle('aora-media-hidden', !visible);
          slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
        });
        this.thumbs.forEach((thumb) => {
          const visible = !!fallback && thumb.dataset.target === fallback.dataset.mediaId;
          thumb.hidden = !visible;
          thumb.classList.toggle('aora-media-hidden', !visible);
          thumb.setAttribute('aria-hidden', visible ? 'false' : 'true');
        });
        this.visibleSlides = fallback ? [fallback] : [];
      }

      let startIndex = forceStart ? 0 : Math.min(this.index, Math.max(0, this.visibleSlides.length - 1));
      if (mapped?.mediaId) {
        const featuredIndex = this.visibleSlides.findIndex((slide) => String(slide.dataset.mediaId || '').endsWith('-' + mapped.mediaId));
        if (featuredIndex >= 0) startIndex = featuredIndex;
      }

      this.updateColorSubtitle(mapped);
      this.show(startIndex, smooth, true);
    }

    updateColorSubtitle(mapped) {
      const subtitle = this.productInfo.querySelector('[data-aora-selected-color]');
      if (subtitle && mapped?.color) subtitle.textContent = mapped.color;
    }

    show(index, smooth = true, fromFilter = false) {
      if (!this.visibleSlides.length || !this.mainList) return;
      const max = this.visibleSlides.length - 1;
      const nextIndex = Math.max(0, Math.min(index, max));
      this.index = nextIndex;
      const active = this.visibleSlides[nextIndex];
      const activeImage = active.querySelector('img');
      if (activeImage) {
        activeImage.loading = 'eager';
        activeImage.fetchPriority = 'high';
      }

      this.visibleSlides.forEach((slide, i) => slide.classList.toggle('is-active', i === nextIndex));
      this.slides.filter((slide) => slide.hidden).forEach((slide) => slide.classList.remove('is-active'));
      this.thumbs.forEach((thumb) => {
        const button = thumb.querySelector('.thumbnail');
        if (!button) return;
        if (!thumb.hidden && thumb.dataset.target === active.dataset.mediaId) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      this.programmaticScroll = true;
      this.mainList.scrollTo({ left: active.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      clearTimeout(this.programmaticTimer);
      this.programmaticTimer = setTimeout(() => {
        this.programmaticScroll = false;
        this.syncIndexFromScroll();
      }, smooth ? 520 : 40);

      this.updateCounter();
      this.refreshControls();
      if (!fromFilter) this.gallery.dataset.aoraManualNavigation = 'true';
    }

    updateCounter() {
      this.gallery.querySelectorAll('.slider-counter--current').forEach((n) => n.textContent = String(this.index + 1));
      this.gallery.querySelectorAll('.slider-counter--total').forEach((n) => n.textContent = String(this.visibleSlides.length));
    }

    refreshControls() {
      const prev = this.gallery.querySelector('[id^="GalleryViewer-"] .slider-button--prev');
      const next = this.gallery.querySelector('[id^="GalleryViewer-"] .slider-button--next');
      if (prev) {
        prev.disabled = this.visibleSlides.length <= 1 || this.index <= 0;
        prev.setAttribute('aria-disabled', prev.disabled ? 'true' : 'false');
      }
      if (next) {
        next.disabled = this.visibleSlides.length <= 1 || this.index >= this.visibleSlides.length - 1;
        next.setAttribute('aria-disabled', next.disabled ? 'true' : 'false');
      }
    }

    syncIndexFromScroll() {
      if (this.programmaticScroll || !this.visibleSlides.length || !this.mainList) return;
      let best = 0;
      let distance = Infinity;
      for (let i = 0; i < this.visibleSlides.length; i += 1) {
        const d = Math.abs(this.visibleSlides[i].offsetLeft - this.mainList.scrollLeft);
        if (d < distance) { distance = d; best = i; }
      }
      if (best !== this.index) {
        this.index = best;
        this.visibleSlides.forEach((slide, i) => slide.classList.toggle('is-active', i === best));
        this.updateCounter();
        this.refreshControls();
      }
    }

    bindGalleryEvents() {
      const signal = this.galleryAbort.signal;

      this.gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('.thumbnail-list__item');
        if (thumb && !thumb.hidden) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const idx = this.visibleSlides.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.target);
          if (idx >= 0) this.show(idx, true);
          return;
        }

        const button = event.target.closest('.slider-button--prev, .slider-button--next');
        if (!button || !button.closest('[id^="GalleryViewer-"]')) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (button.disabled) return;
        this.show(this.index + (button.classList.contains('slider-button--next') ? 1 : -1), true);
      }, { capture: true, signal });

      this.mainList?.addEventListener('scroll', () => {
        if (this.programmaticScroll) return;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.syncIndexFromScroll(), 90);
      }, { passive: true, signal });

      this.mainList?.addEventListener('touchstart', () => {
        this.programmaticScroll = false;
        clearTimeout(this.programmaticTimer);
      }, { passive: true, signal });
    }

    bindProductEvents() {
      const signal = this.abort.signal;
      this.productInfo.addEventListener('change', (event) => {
        if (!event.target.closest('variant-selects')) return;
        this.index = 0;
        [0, 40, 120, 260].forEach((delay) => setTimeout(() => this.filter(false, true), delay));
      }, { signal });

      this.productInfo.addEventListener('nakr:gallery-updated', () => {
        this.index = 0;
        this.attachGallery();
        this.filter(false, true);
      }, { signal });

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId || '') !== String(this.productInfo.dataset.section || '')) return;
          this.index = 0;
          [0, 60, 180].forEach((delay) => setTimeout(() => this.filter(false, true), delay));
        });
      }

      this.productObserver = new MutationObserver((mutations) => {
        const galleryChanged = mutations.some((m) => [...m.addedNodes, ...m.removedNodes].some((node) =>
          node.nodeType === 1 && (node.matches?.('media-gallery, li[data-media-id]') || node.querySelector?.('media-gallery, li[data-media-id]'))
        ));
        if (!galleryChanged) return;
        clearTimeout(this.mutationTimer);
        this.mutationTimer = setTimeout(() => {
          this.attachGallery();
          this.filter(false, false);
        }, 45);
      });
      this.productObserver.observe(this.productInfo, { childList: true, subtree: true });
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('product-info').forEach((productInfo) => {
      if (productInfo.dataset.aoraGalleryReady === 'true') return;
      productInfo.dataset.aoraGalleryReady = 'true';
      new AoraVariantGallery(productInfo);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
